import { STORAGE_KEYS } from "../constants";
import {
  addScoreRecord,
  qualifiesForLeaderboard,
  rankOfSubmission,
  toPublicScores,
  topScores,
  validateSubmission,
  type PublicScore,
  type ScoreRecord,
} from "../logic/leaderboard";

export interface LeaderboardFetchResult {
  scores: PublicScore[];
  source: "netlify" | "local-fallback";
  error?: string;
  notice?: string;
}

export interface SubmitResult extends LeaderboardFetchResult {
  rank: number | null;
  retained: boolean;
}

const FUNCTION_URL = "/.netlify/functions/highscores";

function readLocal(): ScoreRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.localScores);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as ScoreRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(records: ScoreRecord[]): void {
  localStorage.setItem(STORAGE_KEYS.localScores, JSON.stringify(topScores(records)));
}

export async function fetchHighScores(): Promise<LeaderboardFetchResult> {
  try {
    const response = await fetch(FUNCTION_URL, { method: "GET" });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = (await response.json()) as { scores?: PublicScore[] };
    return {
      scores: data.scores ?? [],
      source: "netlify",
    };
  } catch {
    return {
      scores: toPublicScores(readLocal()),
      source: "local-fallback",
      error: "Using local scores only. Shared high scores need Netlify Functions.",
    };
  }
}

export async function submitHighScore(initials: string, score: number): Promise<SubmitResult> {
  const validated = validateSubmission({ initials, score });
  if ("error" in validated) {
    return {
      scores: toPublicScores(readLocal()),
      source: "local-fallback",
      rank: null,
      retained: false,
      error: validated.error,
    };
  }

  try {
    const response = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(validated),
    });
    const data = (await response.json()) as {
      scores?: PublicScore[];
      rank?: number | null;
      retained?: boolean;
      error?: string;
    };
    if (!response.ok) {
      throw new Error(data.error ?? `HTTP ${response.status}`);
    }
    return {
      scores: data.scores ?? [],
      source: "netlify",
      rank: data.rank ?? null,
      retained: data.retained === true,
    };
  } catch (error) {
    const local = readLocal();
    const record: ScoreRecord = {
      initials: validated.initials,
      score: validated.score,
      submittedAt: Date.now(),
    };
    const next = addScoreRecord(local, record);
    const retained = next.some(
      (item) =>
        item.initials === record.initials &&
        item.score === record.score &&
        item.submittedAt === record.submittedAt,
    );
    if (retained) {
      writeLocal(next);
    }
    return {
      scores: toPublicScores(next),
      source: "local-fallback",
      rank: retained ? rankOfSubmission(record, next) : null,
      retained,
      error: retained
        ? undefined
        : error instanceof Error
          ? `${error.message} Could not save this score.`
          : "Could not save this score.",
      notice: retained ? "Saved locally — not production data." : undefined,
    };
  }
}

export async function previewQualification(score: number): Promise<{
  qualifies: boolean;
  source: LeaderboardFetchResult["source"];
  scores: PublicScore[];
}> {
  const result = await fetchHighScores();
  const records: ScoreRecord[] = result.scores.map((entry, index) => ({
    initials: entry.initials,
    score: entry.score,
    submittedAt: index,
  }));
  return {
    qualifies: qualifiesForLeaderboard(score, records),
    source: result.source,
    scores: result.scores,
  };
}
